import { useState, useEffect } from 'react';
import { useLicense } from '../context/LicenseContext';
import { getCompanyLicenses } from '../api/apiService';

/**
 * Safely use license context if available, otherwise fetch directly
 * This allows components to work both inside and outside LicenseProvider
 */
export const useLicenseSafe = (shouldFetch: boolean = false) => {
  const [localLicenseInfo, setLocalLicenseInfo] = useState<any>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Try to use context
  let contextLicense: ReturnType<typeof useLicense> | null = null;
  try {
    contextLicense = useLicense();
  } catch {
    // Not in LicenseProvider, will use local state
  }

  // If context is available, use it
  if (contextLicense) {
    return {
      licenseInfo: contextLicense.licenseInfo,
      isLoading: contextLicense.isLoading,
      error: contextLicense.error,
      refreshLicenses: contextLicense.refreshLicenses,
    };
  }

  // Otherwise, fetch directly when shouldFetch is true
  useEffect(() => {
    if (shouldFetch && !localLicenseInfo && !localLoading) {
      setLocalLoading(true);
      setLocalError(null);
      getCompanyLicenses()
        .then(response => {
          if (response.ok) {
            setLocalLicenseInfo(response.data);
          } else {
            setLocalError('Failed to fetch licenses');
            setLocalLicenseInfo(null);
          }
        })
        .catch(error => {
          setLocalError(error.message || 'Error fetching licenses');
          setLocalLicenseInfo(null);
        })
        .finally(() => setLocalLoading(false));
    }
  }, [shouldFetch, localLicenseInfo, localLoading]);

  return {
    licenseInfo: localLicenseInfo,
    isLoading: localLoading,
    error: localError,
    refreshLicenses: async () => {
      setLocalLoading(true);
      setLocalError(null);
      try {
        const response = await getCompanyLicenses();
        if (response.ok) {
          setLocalLicenseInfo(response.data);
        } else {
          setLocalError('Failed to fetch licenses');
        }
      } catch (error: any) {
        setLocalError(error.message || 'Error fetching licenses');
      } finally {
        setLocalLoading(false);
      }
    },
  };
};

